/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import {Icon} from '@iconify/react';

import React from 'react';

import {useTranslation} from 'react-i18next';

import {BolmateProductRow, BolmateSectionCard} from '../../../controls';

import {OverviewAdSale} from '../../../../util/interfaces';

import {formatCurrency} from '../../../../util/utils';

import {OverviewSectionState} from '../useOverviewSection';

export default function AdRevenue(props: OverviewSectionState<OverviewAdSale>) {
    const {items, loading, loadingMore, error, hasMore, onLoadMore} = props;
    const {t} = useTranslation();

    function formatPercentage(value: number): string {
        return `${value.toFixed(1).replace('.', ',')}%`;
    }

    function renderStats(item: OverviewAdSale) {
        const parts = [
            `${t('overview.adRevenue.costs', 'Kosten')}:\u00A0${formatCurrency(item.costs)}`,
            `ACoS\u00A0${formatPercentage(item.acos)}`
        ];
        if (item.sales_share != null)
            parts.push(
                t('overview.adRevenue.salesShare', '{{share}} van alle verkopen', {
                    share: formatPercentage(item.sales_share)
                })
            );
        return parts.join('\u00A0• ');
    }

    function renderRow(item: OverviewAdSale, index: number) {
        return (
            <BolmateProductRow
                key={index}
                imageUrl={item.image_url}
                imageBadge={
                    <>
                        <Icon icon='mdi:bullhorn-outline' />
                        {item.quantity}x
                    </>
                }
                imageBadgeTitle={t('overview.adRevenue.badgeTooltip', '{{count}} verkocht via ads', {
                    count: item.quantity
                })}
                imageBadgeColor='purple'
                title={item.title}
                titleHref={item.product_url}
                ean={item.ean}
                primaryStat={{
                    label: t('overview.adRevenue.stat', 'Ad omzet'),
                    value: formatCurrency(item.revenue)
                }}
                secondaryStat={{value: renderStats(item), tone: 'muted'}}
            />
        );
    }

    return (
        <BolmateSectionCard
            title={t('overview.sections.adRevenue', 'Gekocht via ads')}
            loading={loading}
            loadingMore={loadingMore}
            empty={!items.length}
            emptyLabel={t('overview.adRevenue.empty', 'Geen advertentieverkopen in deze periode')}
            error={error ? t('overview.sectionError', 'Deze sectie kon niet geladen worden') : undefined}
            hasMore={hasMore}
            loadMoreLabel={t('overview.loadMore', 'Meer laden')}
            onLoadMore={onLoadMore}
        >
            {items.map(renderRow)}
        </BolmateSectionCard>
    );
}
